import { useState, useEffect } from "react";
import {
  Flame,
  Lock,
  Loader2,
  ExternalLink,
  ArrowRight,
  Star,
  ShoppingCart,
  Crown,
  Clock,
  Zap,
  GraduationCap,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import WatchlistButton from "../components/WatchlistButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

function getCurrentMonthLabel() {
  const d = new Date();
  return `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

const SELLING_MARKETPLACES = [
  "eBay","Amazon","Cdiscount","Aliexpress","Leboncoin","Vinted",
  "Etsy","Fnac","Rakuten","Facebook Marketplace","Temu","TikTok Shop","BackMarket",
];
const isSellingMarketplace = (name) =>
  SELLING_MARKETPLACES.some(m => name?.toLowerCase().includes(m.toLowerCase()));

const getPlatformUrl = (platform, productName) => {
  const q = encodeURIComponent(productName);
  const urls = {
    "eBay": `https://www.ebay.fr/sch/i.html?_nkw=${q}`,
    "Amazon": `https://www.amazon.fr/s?k=${q}`,
    "Vinted": `https://www.vinted.fr/catalog?search_text=${q}`,
    "Leboncoin": `https://www.leboncoin.fr/recherche?text=${q}`,
    "Rakuten": `https://fr.shopping.rakuten.com/search/${q}`,
    "Cdiscount": `https://www.cdiscount.com/search/10/${q}.html`,
    "Fnac": `https://www.fnac.com/SearchResult/ResultList.aspx?Search=${q}`,
  };
  return urls[platform] || `https://www.google.com/search?q=${q}+${encodeURIComponent(platform)}`;
};

export default function Opportunities() {
  const { user } = useAuth();
  const email = user?.email || "";
  const [purchased, setPurchased] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [preview, setPreview] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    checkAccess();
    fetchLastRefresh();
  }, [email]);

  const fetchLastRefresh = async () => {
    try {
      const res = await axios.get(`${API}/opportunities/last-refresh`);
      setLastRefresh(res.data);
    } catch {}
  };

  const checkAccess = async () => {
    setLoading(true);
    try {
      if (email) {
        const res = await axios.get(`${API}/purchase/check?email=${encodeURIComponent(email)}&product_type=opportunities_premium`);
        setPurchased(res.data.purchased);
        if (res.data.purchased) {
          const oppsRes = await axios.get(`${API}/opportunities/premium?email=${encodeURIComponent(email)}`);
          setOpportunities(oppsRes.data.opportunities || []);
        }
      }
      // Always load preview
      const previewRes = await axios.get(`${API}/opportunities/preview`);
      setPreview(previewRes.data.preview || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/purchase/checkout`, {
        origin_url: window.location.origin,
        email,
        product_type: "opportunities_premium",
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error("Erreur lors du paiement");
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#00E676]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
            <Flame className="text-[#FF5722]" size={36} />
            OPPORTUNITÉS
          </h1>
          {lastRefresh?.last_refresh && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-[#0A0A0A]/5 dark:bg-[#00E676]/5 border border-[#0A0A0A]/10 px-3 py-1.5" data-testid="last-refresh-badge">
              <Clock size={12} />
              <span>Mise a jour : {new Date(lastRefresh.last_refresh).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          )}
        </div>
        <p className="text-muted-foreground mt-1 uppercase tracking-wider text-xs sm:text-sm">
          10 offres grosse marge du mois — {getCurrentMonthLabel()} — Mise a jour journaliere
        </p>
      </div>

      {purchased ? (
        /* ===== UNLOCKED VIEW ===== */
        <>
          <div className="neo-card p-4 sm:p-5 border-[#00E676] bg-[#00E676]/5">
            <div className="flex items-center gap-3">
              <Crown className="text-[#FFCC00] flex-shrink-0" size={24} />
              <div>
                <p className="font-bold text-sm">Accès débloqué — {getCurrentMonthLabel()}</p>
                <p className="text-xs text-muted-foreground">
                  10 opportunités sélectionnées par Joseph avec marge minimum de 30%. Mise a jour journaliere.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4" data-testid="premium-opportunities">
            {opportunities.map((opp, idx) => (
              <div
                key={opp.id || idx}
                className="neo-card p-5 relative"
                data-testid={`premium-opp-${idx}`}
              >
                <div className="absolute top-3 right-3 bg-[#00E676] text-[#0A0A0A] border-2 border-[#0A0A0A] px-3 py-1 font-bold transform rotate-2 text-sm shadow-[3px_3px_0px_#0A0A0A]">
                  +{opp.margin_percent}%
                </div>

                <div className="flex gap-4 items-start mb-4 pr-20">
                  {opp.product_image && (
                    <img src={opp.product_image} alt="" className="w-16 h-16 object-contain border border-[#0A0A0A]/20 flex-shrink-0" />
                  )}
                  <div>
                    <p className="font-bold line-clamp-2">{opp.product_name}</p>
                    {opp.source_query && (
                      <span className="text-xs text-muted-foreground">{opp.source_query}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground font-bold">Acheter sur</p>
                    <p className="font-semibold text-sm">{opp.buy_platform}</p>
                    <p className="text-xl font-bold">{opp.buy_price?.toFixed(2)}€</p>
                    <a
                      href={opp.buy_url || getPlatformUrl(opp.buy_platform, opp.product_name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#00E676] inline-flex items-center gap-1 mt-1"
                    >
                      Acheter <ExternalLink size={12} />
                    </a>
                  </div>
                  <div className="flex items-center justify-center">
                    <ArrowRight className="text-[#00E676] hidden sm:block" size={28} />
                  </div>
                  <div className={isSellingMarketplace(opp.sell_platform) ? "bg-[#00E676]/10 border-2 border-[#00E676] p-3 -m-1" : ""}>
                    <p className="text-xs uppercase text-muted-foreground font-bold">Vendre sur</p>
                    <p className={`font-semibold text-sm ${isSellingMarketplace(opp.sell_platform) ? "text-[#00E676]" : ""}`}>
                      {opp.sell_platform}
                      {isSellingMarketplace(opp.sell_platform) && (
                        <span className="ml-1 text-[10px] bg-[#00E676] text-[#0A0A0A] px-1.5 py-0.5 font-bold align-middle">TOP</span>
                      )}
                    </p>
                    <p className="text-xl font-bold text-[#00E676]">{opp.sell_price?.toFixed(2)}€</p>
                    <div className="flex items-center gap-2 mt-1">
                      <a
                        href={opp.sell_url || getPlatformUrl(opp.sell_platform, opp.product_name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#00E676] inline-flex items-center gap-1"
                      >
                        Vendre <ExternalLink size={12} />
                      </a>
                      {isSellingMarketplace(opp.sell_platform) && (
                        <a
                          href="https://www.profimy-academie.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-[#FFCC00] inline-flex items-center gap-1 hover:underline"
                        >
                          <GraduationCap size={10} /> Formation
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 bg-[#0A0A0A] text-white p-3 flex justify-between items-center">
                  <span className="font-bold text-sm uppercase">Profit estimé</span>
                  <div className="flex items-center gap-3">
                    <WatchlistButton product={opp} />
                    <span className="text-[#00E676] font-bold text-lg">+{opp.margin?.toFixed(2)}€</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {opportunities.length === 0 && (
            <div className="neo-card p-8 text-center">
              <Loader2 className="animate-spin text-[#00E676] mx-auto mb-3" size={32} />
              <p className="font-bold">Génération des opportunités en cours...</p>
              <p className="text-sm text-muted-foreground mt-1">Les 10 deals seront disponibles sous peu.</p>
            </div>
          )}
        </>
      ) : (
        /* ===== LOCKED / PAYWALL VIEW ===== */
        <>
          {/* Blurred preview */}
          <div className="relative" data-testid="opportunities-locked">
            <div className="space-y-4 blur-sm pointer-events-none select-none" aria-hidden="true">
              {preview.map((p, idx) => (
                <div key={idx} className="neo-card p-5 relative">
                  <div className="absolute top-3 right-3 bg-[#00E676] text-[#0A0A0A] border-2 border-[#0A0A0A] px-3 py-1 font-bold text-sm">
                    +{p.margin_percent}%
                  </div>
                  <div className="flex gap-4 items-start pr-20">
                    {p.product_image && (
                      <img src={p.product_image} alt="" className="w-16 h-16 object-contain border border-[#0A0A0A]/20" />
                    )}
                    <div>
                      <p className="font-bold">{p.category}</p>
                      <p className="text-sm text-muted-foreground mt-1">Marge: +{p.margin_percent}%</p>
                    </div>
                  </div>
                  <div className="mt-3 bg-[#0A0A0A] text-white p-3">
                    <span className="font-bold text-[#00E676]">+██.██€</span>
                  </div>
                </div>
              ))}
              {preview.length === 0 && [1,2,3].map(i => (
                <div key={i} className="neo-card p-5">
                  <div className="h-20 bg-muted/20 animate-pulse" />
                </div>
              ))}
            </div>

            {/* Overlay CTA */}
            <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm">
              <div className="neo-card p-6 sm:p-8 max-w-lg text-center border-[#00E676] shadow-[0_0_30px_rgba(0,230,118,0.15)]">
                <div className="w-16 h-16 bg-[#00E676] border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-4 shadow-[4px_4px_0px_#0A0A0A]">
                  <Flame size={32} className="text-[#0A0A0A]" />
                </div>
                <h2 className="text-xl sm:text-2xl font-extrabold mb-2">
                  OPPORTUNITÉS GROSSE MARGE
                </h2>
                <p className="text-muted-foreground text-sm mb-4">
                  10 offres exclusives avec <strong>+30% de marge minimum</strong>, selectionnees par <strong>Joseph</strong>.
                  Mise a jour journaliere.
                </p>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-4xl font-extrabold text-[#00E676]">4,90€</span>
                </div>
                <ul className="text-left text-sm space-y-2 mb-6 max-w-xs mx-auto">
                  <li className="flex items-center gap-2"><Zap size={14} className="text-[#00E676] flex-shrink-0" /> 10 opportunites selectionnees par Joseph</li>
                  <li className="flex items-center gap-2"><Star size={14} className="text-[#FFCC00] flex-shrink-0" /> Marge minimum garantie: 30%</li>
                  <li className="flex items-center gap-2"><Clock size={14} className="text-[#00BCD4] flex-shrink-0" /> Mise a jour journaliere</li>
                  <li className="flex items-center gap-2"><ShoppingCart size={14} className="text-[#FF5722] flex-shrink-0" /> Liens directs d'achat et de vente</li>
                </ul>
                <button
                  onClick={handleUnlock}
                  disabled={checkoutLoading}
                  className="neo-button-primary w-full text-base py-3 flex items-center justify-center gap-2"
                  data-testid="unlock-opportunities-btn"
                >
                  {checkoutLoading ? (
                    <><Loader2 size={20} className="animate-spin" /> Redirection...</>
                  ) : (
                    <><Lock size={18} /> Débloquer pour 4,90€</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
